import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { loginSchema, type LoginInput, type LoginResponse } from '@koyomi/shared';
import { Button, Input } from '@koyomi/ui';
import { AuthCard } from './AuthCard.tsx';
import { api, ApiError } from '../../lib/api.ts';
import { useAuthStore } from '../../app/auth-store.ts';

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const setSession = useAuthStore((s) => s.setSession);
  // Autoatendimento ligado no Admin: o login oferece criar conta (cadastro pelo site).
  const signup = useQuery({ queryKey: ['signup-config'], queryFn: () => api<{ selfService: boolean }>('/public/signup/config', { global: true }), staleTime: 60_000 });
  const form = useForm<LoginInput>({ resolver: zodResolver(loginSchema), defaultValues: { email: '', password: '' } });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const res = await api<LoginResponse>('/auth/login', { body: values, global: true });
      setSession(res);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'INTERNAL';
      form.setError('root', { message: t(`common:errors.${code}`, { defaultValue: t('common:errors.INTERNAL') }) });
    }
  });

  return (
    <AuthCard title={t('auth:login.title')} subtitle={t('auth:login.subtitle')}>
      <form className="k-stack" onSubmit={onSubmit} noValidate>
        <Input label={t('auth:login.email')} type="email" autoComplete="email" autoFocus {...form.register('email')} error={form.formState.errors.email && t('common:errors.VALIDATION_ERROR')} />
        <Input label={t('auth:login.password')} type="password" autoComplete="current-password" {...form.register('password')} error={form.formState.errors.password && t('common:errors.VALIDATION_ERROR')} />
        {form.formState.errors.root && <div className="k-field__error" role="alert">{form.formState.errors.root.message}</div>}
        <Button type="submit" variant="primary" size="lg" block loading={form.formState.isSubmitting}>{t('auth:login.submit')}</Button>
        <Link to="/forgot-password" style={{ textAlign: 'center' }}>{t('auth:login.forgot')}</Link>
        {signup.data?.selfService && <p className="k-muted" style={{ textAlign: 'center', margin: 0 }}>{t('auth:login.noAccount')} <Link to="/cadastro">{t('auth:login.createAccount')}</Link></p>}
      </form>
    </AuthCard>
  );
}
